import React from 'react';
import { X } from 'lucide-react';

const FilterPanel = ({
  isOpen,
  onClose,
  leadFieldDefs,
  advancedFilters,
  updateAdvancedFilter,
  filterOperators = ['Contains', 'NotContains', 'StartsWith', 'EndsWith', 'EqualTo'],
  sourceOptions = [],
  mediumOptions = [],
  campaignOptions = [],
  followupOptions = [],
  userOptions = [],
  customFields = [],
  applyAdvancedFilters,
  clearAdvancedFilters,
}) => {
  const renderFilterInput = (key, label, placeholder = '') => {
    const item = advancedFilters[key];
    if (!item) return null;
    return (
      <div key={key} className="mb-4 border-b border-gray-100 pb-3 px-1">
        <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={item.enabled}
            onChange={(e) => updateAdvancedFilter(key, { enabled: e.target.checked })}
            className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
          />
          <span>{label}</span>
        </label>
        {item.enabled && (
          <div className="space-y-2 mt-2">
            <select
              value={item.operator}
              onChange={(e) => updateAdvancedFilter(key, { operator: e.target.value })}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:outline-none bg-white text-gray-700 cursor-pointer"
            >
              {filterOperators.map((operator) => (
                <option key={operator} value={operator}>{operator}</option>
              ))}
            </select>
            <input
              type="text"
              value={item.value}
              onChange={(e) => updateAdvancedFilter(key, { value: e.target.value })}
              placeholder={placeholder || label}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:outline-none bg-white text-gray-800"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      id="mySidenav"
      className="fixed left-[60px] top-0 h-full bg-white border-r border-gray-200 z-[9999] transition-all duration-300 overflow-hidden flex flex-col shadow-lg"
      style={{ width: isOpen ? '225px' : '0px' }}
    >
      <div className="h-full flex flex-col w-[225px]">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-3 border-b border-gray-200 bg-gray-50">
          <p className="text-sm font-semibold text-gray-700">Filters</p>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-200 rounded transition"
            title="Close panel"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Scrollable Filters List */}
        <div className="flex-1 overflow-y-auto px-3 py-3 custom-scrollbar">
          {leadFieldDefs.map((f) => {
            if (['sourceName', 'mediumName', 'campaignName', 'followUpStatus', 'assignedTo'].includes(f.key)) {
              const item = advancedFilters[f.key] || { enabled: false, operator: 'Contains', value: '' };
              let options = [];
              if (f.key === 'sourceName') options = sourceOptions;
              if (f.key === 'mediumName') options = mediumOptions;
              if (f.key === 'campaignName') options = campaignOptions;
              if (f.key === 'followUpStatus') options = followupOptions;
              if (f.key === 'assignedTo') options = userOptions;

              return (
                <div key={f.key} className="mb-4 border-b border-gray-100 pb-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={item.enabled}
                      onChange={(e) => updateAdvancedFilter(f.key, { enabled: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span>{f.label}</span>
                  </label>
                  {item.enabled && (
                    <div className="space-y-2">
                      <select
                        value={item.operator}
                        onChange={(e) => updateAdvancedFilter(f.key, { operator: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:outline-none bg-white text-gray-700 cursor-pointer"
                      >
                        {filterOperators.map((operator) => (
                          <option key={operator} value={operator}>{operator}</option>
                        ))}
                      </select>

                      <select
                        value={item.value}
                        onChange={(e) => updateAdvancedFilter(f.key, { value: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:outline-none bg-white text-gray-700 cursor-pointer"
                      >
                        <option value="">-- Select {f.label} --</option>
                        {options.map((opt, i) => (
                          <option key={i} value={opt.SrcmedCampID || opt.Code || opt.Value || ''}>
                            {opt.SrcmedCampName || opt.Text || opt.Name || ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              );
            }
            return renderFilterInput(f.key, f.label, `Enter ${f.label}`);
          })}

          {/* Custom Fields Filters */}
          {customFields.length > 0 && (
            <div className="mt-2">
              <p className="text-sm font-semibold text-gray-600 mb-2 border-b border-gray-200 pb-1">Custom Fields</p>
              {customFields.map((f) => {
                const id = f.FieldId ?? f.field_ID ?? f.Id ?? f.id ?? f.fieldId;
                const key = `custom_${id}`;
                const label = f.FieldName ?? f.FieldLabel ?? f.Name ?? `Field ${id}`;
                return renderFilterInput(key, label);
              })}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-3 py-3 border-t border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={applyAdvancedFilters}
              className="w-full px-3 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 transition shadow-sm"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={clearAdvancedFilters}
              className="w-full px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition shadow-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
